import {Register} from "./RegisterRelatedComponents";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCaretRight} from "@fortawesome/free-solid-svg-icons/faCaretRight";
import {faCaretLeft} from "@fortawesome/free-solid-svg-icons";
import {UnlimitedRegisterMachine} from "../../js/entities/UnlimitedRegisterMachine/UnlimitedRegisterMachine";
import {Fragment, useEffect} from "react";
import {convertStrToInt, exportTextFile} from "../../js/utils";


/**
 *
 * @param runModeState fully-initialized useState-variable of RUN mode state.
 * It contains the following fields: <br/>
 * <code>initialRegisters</code> ({@link number}[]), <br/>
 * <code>urmProgram</code> ({@link UrmProgram}), <br/>
 * <code>history</code> ({@link UrmMemento}[]), <br/>
 * <code>currentIndexInHistory</code> ({@link number}), <br/>
 * <code>stepsLimit</code> ({@link number}).
 *
 * @param setRunModeState set function of <code>runModeState</code>
 * @param switchToCreateMode is callback that changes mode to 'CREATE'. Takes no arguments.
 * @constructor
 */
export default function UrmRunModeComponent({runModeState,
                                                setRunModeState,
                                                switchToCreateMode}){
    const MIN_STEPS_LIMIT = 1;
    const MAX_STEPS_LIMIT = 1_000;


    // here, runModeState is fully initialized
    // rendering page uses only history, currentIndexInHistory, and urmProgram

    // for actually executing URM program
    let urm = new UnlimitedRegisterMachine(runModeState.urmProgram, runModeState.initialRegisters);

    // indicates that calculating full UrmProgram is performing now
    // when it is true, no input from user should be accepted (we are using spinner for this)
//    const [calculatingFullUrmProgram, setCalculatingFullUrmProgram] = useState(false);

    // to focus on (scroll to) nextCommand after each re-render
    useEffect(() => {
        let selectedLines = document.getElementsByClassName('urm-program-line-selected');
        if(selectedLines.length > 0){ // just in case, because selected line always exists, and it is unique
            let selectedLine = selectedLines[0];
            selectedLine.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [runModeState]);



    /**
     * Returns current state that must be displayed.
     *
     * The only method that should be user to get current state (instead of calling history and currentIndexInHistory)
     * @returns {UrmMemento}
     */
    const getCurrentUrmState = () => {
        return runModeState.history[runModeState.currentIndexInHistory];
    }

    const decrementCurrentIndexInHistory = () => {
        setRunModeState((state) => ({
            ...state,
            currentIndexInHistory: state.currentIndexInHistory - 1
        }));
    }

    const incrementCurrentIndexInHistory = () => {
        setRunModeState((state) => ({
            ...state,
            currentIndexInHistory: state.currentIndexInHistory + 1
        }));
    }

    /**
     * @param newMemento {UrmMemento}
     */
    const addNewMementoAndIncrementCurrentIndexInHistory = (newMemento) => {
        setRunModeState((state) => ({
            ...state,
            history: [...state.history, newMemento],
            currentIndexInHistory: state.currentIndexInHistory + 1
        }));
    }


    const onBackToTheInitState = (event) => {
        event.preventDefault();

        setRunModeState((state) => ({
            ...state,
            currentIndexInHistory: 0
        }));
    }

    const onStepBackward = (event) => {
        event.preventDefault();

        if(runModeState.currentIndexInHistory === 0){ // impossible case, because the button must be inactive
            alert('URM is already in initial state');
            return;
        }

        decrementCurrentIndexInHistory();
    }

    const onStepForward = (event) => {
        event.preventDefault();

        if(getCurrentUrmState().isFinished){ // impossible case, because the button must be inactive
            alert('URM is already finished');
            return;
        }

        if(runModeState.currentIndexInHistory < runModeState.history.length - 1){ // history contains result of step forward
            incrementCurrentIndexInHistory();
        }
        else{ // otherwise, really need to take a step
            urm.restoreState(getCurrentUrmState());
            urm.executeNextCommand();
            addNewMementoAndIncrementCurrentIndexInHistory(urm.saveState());
        }
    }

    const onExecuteFullProgramAndNotExceedMaxSteps = (event) => {
        event.preventDefault();

        if(getCurrentUrmState().isFinished){ // impossible case, because the button must be inactive
            alert('URM is already finished');
            return;
        }

        // check, if this calculation have already been done
        if(runModeState.currentIndexInHistory + runModeState.stepsLimit < runModeState.history.length){
            console.log('calc skipped: 1');
            setRunModeState((state) => ({
                ...state,
                currentIndexInHistory: state.currentIndexInHistory + state.stepsLimit
            }));
            return;
        }
        else if(runModeState.history.at(-1).isFinished){
            console.log('calc skipped: 2');
            setRunModeState((state) => ({
                ...state,
                currentIndexInHistory: state.history.length - 1
            }));
            return;
        }
        // otherwise, really need to make calculations


        // setCalculatingFullUrmProgram((_) => true); // turn on calculation banner

        urm.restoreState(getCurrentUrmState());
        let steps = 0;
        let furtherHistory = [];
        while(!urm.isFinished && steps < runModeState.stepsLimit){ // main exec loop
            urm.executeNextCommand();
            furtherHistory.push(urm.saveState());
            steps++;
        }

        let historyUpdated =
            runModeState.history.slice(0, runModeState.currentIndexInHistory + 1) // history up to currentIndexInHistory (inclusively)
                .concat(furtherHistory); // concat history generated by steps

        // update view
        setRunModeState((state) => ({
            ...state,
            history: historyUpdated,
            currentIndexInHistory: historyUpdated.length - 1
        }));

        // setCalculatingFullUrmProgram((_) => true); // turn off calculation banner
    }

/*    if(calculatingFullUrmProgram === true){
        return(
            <LongCalculationComponent calculationContinues={calculatingFullUrmProgram}
                                      setCalculationContinues={setCalculatingFullUrmProgram}
                                      calculation = {executeFullProgramAndNotExceedMaxSteps}/>
        );
    } 
 */


    const onSwitchToCreateMode = (event) => {
        event.preventDefault();

        switchToCreateMode();
    }


    const onExportCalculations = (event) => {
        event.preventDefault();

        if(runModeState.history.length === 0){ // impossible case (history is always non-empty, and button is disabled)
            return;
        }

        let result = '';

        // print URM
        result +=
            "========== URM PROGRAM ==========\n" +
            runModeState.urmProgram.toString() +
            "\n";

        // print all UrmMemento from history
        result += "==========      STEPS     ==========\n\n";
        runModeState.history.forEach((memento, index) => {
            if (index === 0){
                result += `0) (initial, before calculations)\n`;
            }
            else{
                result += `${index})\n`;
            }

            result += `Registers: ${memento.registers.join(' ')}\n`;

            if(!memento.isFinished){
                result += `Next command: ${memento.nextCommandIndex}) ${runModeState.urmProgram
                    .getCommandByIndex(memento.nextCommandIndex).toString()}\n`;
            }
            else{
                result += `Next command: ${memento.nextCommandIndex}) [Does not exist] - The stopping condition is met \n`;
            }

            result += "\n\n";
        });

        // add info about finishing; check info about last memento
        if(runModeState.history.at(-1).isFinished){
            result += "FINISHED";
        }
        else{
            result += "NOT FINISHED";
        }

        exportTextFile("urm_calculations.txt", result);
    }


    return (
        <>

            {/* Registers */}
            <h3>Registers</h3>
            <div className={'registers-container'}>
                {
                    getCurrentUrmState().registers.map((register, index) => (
                        <Register key={index} content={register} index={index}/>
                    ))
                }
            </div>

            <div className="row">
                <div className="col-6"><h3>URM program</h3></div>
                <div className="col-6"><h3></h3></div>
            </div>

            <div className="row">

                {/* URM program */}
                <div className="col-6">
                    <div className="urm-program-code-wrapper-run-mode">
                        {
                            runModeState.urmProgram
                                .toString()
                                .split('\n')
                                .map((line, index) =>
                                    <div className={(getCurrentUrmState().nextCommandIndex === index + 1) ?
                                        'urm-program-line urm-program-line-selected px-2 text-start' :
                                        'urm-program-line px-2 text-start'}
                                         key={index}>
                                        {line}
                                    </div>
                                )
                        }
                        { // additional line, when next command > maximum command (this means that URM is finished)
                            (getCurrentUrmState().nextCommandIndex > runModeState.urmProgram.lastCommandIndex) &&
                            <div className={'urm-program-line urm-program-line-selected px-2 text-start'}>
                                {<Fragment>&nbsp;</Fragment>}
                            </div>
                        }
                    </div>
                </div>


                {/* info about URM execution, and controls to manage URM */}
                <div className="col-6">

                    {/* show executedCommandsCount */}
                    <div className="row">
                        <div className="col-6 p-1">
                            <p className="text-end m-0"><b>EXECUTED COMMANDS:</b></p>
                        </div>
                        <div className="col-6 p-1">
                            <p className="text-start m-0">{getCurrentUrmState().executedCommandsCount}</p>
                        </div>
                    </div>

                    {/* show nextCommandIndex */}
                    <div className="row">
                        <div className="col-6 p-1">
                            <p className="text-end m-0"><b>NEXT COMMAND:</b></p>
                        </div>
                        <div className="col-6 p-1">
                            <p className="text-start m-0">{getCurrentUrmState().nextCommandIndex}</p>
                        </div>
                    </div>

                    {/* Shows if URM finished */}
                    {(getCurrentUrmState().isFinished) ? (
                        <p className={'m-0 urm-is-finished-label urm-is-finished-true'}>FINISHED</p>
                    ) : (
                        <p className={'m-0 urm-is-finished-label urm-is-finished-false'}>NOT FINISHED</p>
                    )}


                    {/* exec full program button, and input for stepsLimit */}
                    <div className="row align-items-center mt-5">
                        <div className="col-md-6 p-0 text-end">
                            {/* execute full URM program button, but not exceed maximum number of steps */}
                            <button type="button" className="btn btn-primary btn-lg m-1"
                                    disabled={getCurrentUrmState().isFinished}
                                    onClick={onExecuteFullProgramAndNotExceedMaxSteps}>
                                <p className={'m-0 p-0'}>{'RUN FULL PROGRAM'}</p>
                            </button>
                        </div>

                        {/* input of stepsLimit */}
                        <div className="col-md-6 row align-content-center p-0 text-start">
                            <div className="col text-end pr-1">
                                Steps limit:
                            </div>
                            <div className="col text-start">
                                <input id='steps-limit-input' type='number' required
                                       min={MIN_STEPS_LIMIT} max={MAX_STEPS_LIMIT} step='1'
                                       value={runModeState.stepsLimit}
                                       className='steps-limit-input'

                                       onChange={(e) => {
                                           e.preventDefault();
                                           setRunModeState((state) => ({
                                               ...state,
                                               stepsLimit: Math.min(
                                                   Math.max(convertStrToInt(e.target.value, MIN_STEPS_LIMIT), MIN_STEPS_LIMIT),
                                                   MAX_STEPS_LIMIT)
                                           }));
                                       }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-center">
                        {/* button for back to the initial state of URM program */}
                        <button type="button" className="btn btn-primary btn-lg m-1 mb-0"
                                disabled={runModeState.currentIndexInHistory === 0}
                                onClick={onBackToTheInitState}>
                            <p className={'m-0 p-0'}>{'TO START'}</p>
                        </button>

                        {/* one step backward */}
                        <button type="button" className="btn btn-primary btn-lg m-1 mb-0"
                                disabled={runModeState.currentIndexInHistory === 0}
                                onClick={onStepBackward}>
                            <p className={'m-0 p-0'}><FontAwesomeIcon icon={faCaretLeft}/> step</p>
                        </button>

                        {/* one step forward */}
                        <button type="button" className="btn btn-primary btn-lg m-1 mb-0"
                                disabled={getCurrentUrmState().isFinished}
                                onClick={onStepForward}>
                            <p className={'m-0 p-0'}>step <FontAwesomeIcon icon={faCaretRight}/></p>
                        </button>
                    </div>

                    {/* info for debug
                    <p className={'m-0 text-start'}><b>currentIndexInHistory</b>: {runModeState.currentIndexInHistory}</p>
                    <p className={'m-0 text-start'}><b>history.length</b>: {runModeState.history.length}</p>
                    */}
                </div>
            </div>


            <div className="d-flex flex-row justify-content-around mt-2 mb-5">
                {/* Export calculations */}
                <button type="button" className="btn btn-primary btn-lg m-2 mb-0"
                        disabled={runModeState.history.length === 0}
                        onClick={onExportCalculations}>
                    <p className={'m-0 p-0'}>Export calculations</p>
                </button>

                {/* button to go to CREATE mode */}
                <button type="button" className="btn btn-primary btn-lg m-2 mb-0"
                        disabled={false}
                        onClick={onSwitchToCreateMode}>
                    <p className={'m-0 p-0'}>Back to CREATE mode</p>
                </button>
            </div>

        </>
    );
}